// components/subscription/SubscriptionSection.jsx
import React, { useState, useMemo } from 'react';
import { Lock, AlertCircle, ExternalLink, CreditCard, Loader2, Clock, ArrowRight } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import { useTheme } from '@/managers/ThemeProvider';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import WorkspacePricingChart from '@/components/marketing/landingpage/WorkspacePricingChart';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/firebase/config';

const CLOUD_SYNC_PRICE_ID = 'price_1RVwrmEZWDfPdaNVX6uRAkz9';

const SecurityBadge = ({ children }) => (
  <div className="inline-flex items-center gap-1.5 px-2 py-1
                  bg-primary/10 rounded-full text-xs text-primary-light">
    <Lock className="w-3 h-3" />
    {children}
  </div>
);

/* ========================
 * SubscriptionSection - Manages subscription display and actions.
 * Props:
 * - user: object - The authenticated user object from Firebase.
 * - dbUserProfile: object or null - The user's profile data from Firestore.
 * - userTier: string - The user's current plan tier.
 * - isOnFreeTrial: boolean - True if the user is on a free trial.
 * - daysRemaining: number - Number of days left in the trial.
 * - trialEndsAt: Date object or null - The exact date the trial ends.
 * ======================== */
export const SubscriptionSection = ({ user, dbUserProfile, userTier, isOnFreeTrial, daysRemaining, trialEndsAt }) => {
  const { isLightTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const navigate = useNavigate();

  const hasActivePaidSubscription = useMemo(() =>
    ['Plus', 'Pro'].includes(userTier) && !isOnFreeTrial,
    [userTier, isOnFreeTrial]
  );

  const clearIndexedDB = async () => {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.deleteDatabase('firebaseLocalStorageDb');
      request.onerror = (event) => reject(new Error('Failed to clear IndexedDB: ' + event.target.errorCode));
      request.onsuccess = () => resolve();
    });
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );
    if (!confirmed) return;

    setIsDeletingAccount(true);
    try {
      const currentUser = authManager.getCurrentUser(); // Re-fetch to ensure current state
      if (!currentUser || currentUser.uid !== user.uid) {
        throw new Error('User authentication error or mismatch.');
      }

      // Delete Firestore document first
      await deleteDoc(doc(db, 'users', currentUser.uid));

      // Clear local storage
      await clearIndexedDB(); // Clear IndexedDB
      localStorage.clear();   // Clear localStorage
      sessionStorage.clear(); // Clear sessionStorage

      // Delete Firebase auth user
      await currentUser.delete();

      // Explicitly sign out from authManager, though currentUser.delete() might handle it
      await authManager.signOut();

      toast.success('Account successfully deleted. You have been signed out.');
      navigate('/');
      window.location.reload(); // Force a full reload to clear all app state
    } catch (error) {
      console.error('Delete account error:', error);
      let message = 'Failed to delete account.';
      if (error.code === 'auth/requires-recent-login') {
        message = 'This operation is sensitive and requires recent authentication. Please sign out and sign back in to delete your account.';
      } else if (error.message) {
        message = `Failed to delete account: ${error.message}`;
      }
      toast.error(message);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleCloudSubscription = async () => {
    if (hasActivePaidSubscription) {
      await redirectToPortal();
      return;
    }

    setIsLoading(true);

    try {
      const currentUser = authManager.getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated.');
      }

      const token = await currentUser.getIdToken();
      const origin = window.location.origin;
      const response = await fetch('https://createcheckoutsession-5g32mji35q-uc.a.run.app', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: CLOUD_SYNC_PRICE_ID,
          successUrl: `${origin}/settings/subscription?action=success`,
          cancelUrl: `${origin}/settings/subscription?action=cancel`,
          metadata: { planType: 'Cloud-Synced', userId: currentUser.uid }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to start cloud subscription.');
      }

      const { url } = await response.json();
      if (!url) {
        throw new Error('No checkout URL received.');
      }

      window.location.href = url;
    } catch (error) {
      console.error('Cloud subscription error:', error);
      toast.error(error.message || 'Failed to start cloud subscription.');
      setIsLoading(false);
    }
  };

  const redirectToPortal = async () => {
    if (!hasActivePaidSubscription) {
      toast.error("No active subscription to manage.");
      // Optionally navigate to upgrade page if they are free/trial and somehow click this
      // navigate('/upgrade');
      return;
    }

    setIsLoading(true);
    try {
      const currentUser = authManager.getCurrentUser();
      if (!currentUser) throw new Error('User not authenticated for portal access.');

      const token = await currentUser.getIdToken();
      const origin = window.location.origin;

      const response = await fetch('https://createportalsession-5g32mji35q-uc.a.run.app', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: `${origin}/settings/subscription` }) // Return to subscription tab
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Portal access failed (${response.status})`);
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
        return;
      } else {
        throw new Error('No portal URL received.');
      }
    } catch (err) {
      console.error("Error redirecting to portal:", err);
      toast.error(err.message || "Could not access the subscription management portal.");
    } finally {
      setIsLoading(false);
    }
  };

  const descriptionText = useMemo(() => {
    if (isOnFreeTrial) {
      return `You are currently enjoying a Pro trial with ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining. Choose a plan below to continue your service.`;
    }
    if (userTier === 'Free') {
      return 'You are on the Free Tier. Upgrade to unlock more features!';
    }
    return 'Manage your plan and billing details below.';
  }, [isOnFreeTrial, userTier, daysRemaining]);

  const planDisplayText = useMemo(() => {
    if (isOnFreeTrial) {
      return `Pro (Trial)`;
    }
    return userTier ? userTier.charAt(0).toUpperCase() + userTier.slice(1) : 'Free';
  }, [isOnFreeTrial, userTier]);

  const billDateText = useMemo(() => {
    if (isOnFreeTrial) {
      return trialEndsAt ? `Trial ends on ${trialEndsAt.toLocaleDateString()}` : 'N/A';
    }
    // Use dbUserProfile for subscriptionCurrentPeriodEnd
    if (dbUserProfile && dbUserProfile.subscriptionCurrentPeriodEnd) {
      return `Renews on ${new Date(dbUserProfile.subscriptionCurrentPeriodEnd * 1000).toLocaleDateString()}`;
    }
    return 'N/A';
  }, [isOnFreeTrial, trialEndsAt, dbUserProfile]);

  const planLabel = userTier ? userTier.charAt(0).toUpperCase() + userTier.slice(1) : 'Free';

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className={`rounded-xl border border-border-color overflow-hidden
                     ${isLightTheme ? 'bg-white' : 'bg-surface-raised'}`}>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h3 className="text-lg font-medium text-on-surface">Current Subscription</h3>
              <p className="text-sm text-on-surface/60">{descriptionText}</p>
            </div>

            <div className="bg-surface p-3 rounded-lg border border-border-subtle min-w-[220px]">
              <div className="text-xs text-on-surface-dim mb-1">Current Plan</div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-on-surface bg-primary px-2 py-0.5 rounded">
                    {planLabel}
                  </span>
                  {isOnFreeTrial && (
                    <span className="text-xs text-success bg-success/10 px-1.5 py-0.5 rounded">
                      Trial - {daysRemaining} days left
                    </span>
                  )}
                </div>

                {!isOnFreeTrial && userTier === 'Free' && (
                  <button
                    onClick={handleCloudSubscription}
                    className="text-xs text-primary flex items-center flex-shrink-0"
                  >
                    Get Pro Cloud <ArrowRight className="w-3 h-3 ml-1" />
                  </button>
                )}
                {isOnFreeTrial && daysRemaining <= 7 && (
                  <button
                    onClick={handleCloudSubscription}
                    className="text-xs text-primary flex items-center flex-shrink-0"
                  >
                    Continue <ArrowRight className="w-3 h-3 ml-1" />
                  </button>
                )}
              </div>

              {isOnFreeTrial && daysRemaining <= 3 && (
                <div className="mt-2 pt-2 border-t border-border-subtle">
                  <div className="flex items-center gap-2 text-xs text-warning font-medium">
                    <Clock className="w-3 h-3" />
                    Trial ending soon.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div className="space-y-1">
              <label className="text-xs text-on-surface/60">{isOnFreeTrial ? 'Trial End Date' : 'Next Bill Date'}</label>
              <p className="text-sm font-medium text-on-surface">{billDateText}</p>
            </div>
            <div className="space-y-1">
              {hasActivePaidSubscription && (
                <button
                  onClick={redirectToPortal}
                  disabled={isLoading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary rounded-lg text-white hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0 shadow-sm"
                >
                  {isLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Accessing...</>) : (<><CreditCard className="w-4 h-4" /> Manage Billing</>)}
                </button>
              )}
            </div>
          </div>
          {isOnFreeTrial && daysRemaining <= 3 && (
            <div className="mt-3 pt-3 border-t border-border-subtle">
              <div className="flex items-center gap-2 text-sm p-3 rounded-md bg-surface">
                <Clock className="w-5 h-5 text-warning flex-shrink-0" />
                <span className="text-warning font-medium">Your Pro trial is ending soon! Subscribe to a plan to keep your Pro features.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <WorkspacePricingChart
        variant="settings"
        onLocalAction={undefined}
        onCloudAction={handleCloudSubscription}
        localActionLabel={hasActivePaidSubscription ? 'Local Free' : 'Included'}
        cloudActionLabel={hasActivePaidSubscription ? 'Manage Billing' : isOnFreeTrial ? 'Secure Cloud Plan' : 'Get Pro'}
        localActionDisabled={true}
        cloudActionDisabled={isLoading}
      />

      <div className="flex justify-center pt-0 pb-6">
        <Link to="/privacy" className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80">
          <ExternalLink className="w-4 h-4" /> Terms & Privacy Policy
        </Link>
      </div>

      <section className="space-y-1 mt-4 opacity-70">
        <h2 className="text-xs font-medium text-on-surface/70">Permanent Actions</h2>
        <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3">
          <div className="flex items-start gap-2 mb-2">
            <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-xs font-medium text-red-400">Delete Account</h3>
              <p className="text-xs text-on-surface/50 mt-0.5">This will permanently delete your account and all associated data. This action cannot be undone.</p>
            </div>
          </div>
          <button
            onClick={handleDeleteAccount}
            disabled={isDeletingAccount}
            className="w-full px-3 py-2 text-xs text-red-400 hover:text-red-300 border border-red-500/10 rounded-lg hover:bg-red-500/5 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 min-h-[36px]"
          >
            {isDeletingAccount ? (<><Loader2 className="w-3 h-3 animate-spin" /> Deleting Account...</>) : ('Delete Account')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default SubscriptionSection;